import Head from "next/head";
import Link from "next/link";
import React from "react";
import { useIsUserLoggedIn } from "#next/hooks/use-is-user-logged-in";
import { useLogout } from "#next/hooks/use-logout";
import { useSession } from "@supabase/auth-helpers-react";
import { useRouter } from "next/router";

export const Header: React.FC = () => {
  const session = useSession();
  const logout = useLogout();
  const { isLoggedIn } = useIsUserLoggedIn();
  const router = useRouter();

  // Determine current page
  const currentPath = router.pathname;

  return (
    <>
      <Head>
        <title>Dom Ghost | DOM Access & AI Tools</title>
        <meta name="description" content="Control and manage your Dom Ghost browser extension and API tokens" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <header className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white py-5 shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 bg-repeat"></div>

        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[ ...Array(10) ].map((_, i) => (
            <div
              key={i}
              className={`absolute rounded-full bg-white/20 ${isLoggedIn ? "animate-float" : ""}`}
              style={{
                width: "8px",
                height: "8px",
                top: `${(i * 5) % 100}%`,
                left: `${((i * 7) + 10) % 100}%`,
                animationDelay: `${i * 0.5}s`
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-4 flex justify-between items-center relative z-10">
          {/* Logo and Title */}
          <Link href="/" className="flex items-center space-x-3" prefetch>
            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-6 w-6 text-white"
              >
                <path d="m6 9 6-6 6 6"/>
                <path d="m6 15 6 6 6-6"/>
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-100 drop-shadow-md hover:scale-105 transition-transform">Dom Ghost</h1>
              <p className="text-xs text-purple-200 font-medium tracking-wide">DOM ACCESS & AI INTEGRATION</p>
            </div>
          </Link>

          {/* Navigation Buttons - Always displayed */}
          <div className="flex items-center space-x-2 md:space-x-3">
            {/* Quickstart Button */}
            <Link
              prefetch
              href="/quickstart"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg backdrop-blur-md text-white font-medium border shadow-md ${
                currentPath === "/quickstart" 
                  ? "bg-white/30 border-white/40 cursor-not-allowed" 
                  : "bg-white/10 border-white/20 hover:bg-white/20 hover:shadow-lg hover:scale-105 transition-all duration-300"
              }`}
              aria-disabled={currentPath === "/quickstart"}
              tabIndex={currentPath === "/quickstart" ? -1 : 0}
              onClick={e => currentPath === "/quickstart" && e.preventDefault()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="flex-shrink-0"
              >
                <path d="m6 9 6-6 6 6"/>
                <path d="m6 15 6 6 6-6"/>
              </svg>
              <span>Quickstart</span>
            </Link>
            
            {/* Pricing Button */}
            <Link
              prefetch
              href="/pricing"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg backdrop-blur-md text-white font-medium border shadow-md ${
                currentPath === "/pricing" 
                  ? "bg-white/30 border-white/40 cursor-not-allowed" 
                  : "bg-white/10 border-white/20 hover:bg-white/20 hover:shadow-lg hover:scale-105 transition-all duration-300"
              }`}
              aria-disabled={currentPath === "/pricing"}
              tabIndex={currentPath === "/pricing" ? -1 : 0}
              onClick={e => currentPath === "/pricing" && e.preventDefault()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="flex-shrink-0"
              >
                <path d="M15 3a6 6 0 0 0-6 6c0 2.17.974 4.103 2.5 5.444C13.42 16.295 15 18.044 15 21h0a6 6 0 0 0 6-6c0-2.17-.974-4.103-2.5-5.444C16.58 7.705 15 5.956 15 3h0z"/>
                <path d="M9 3a6 6 0 0 1 6 6c0 2.17-.974 4.103-2.5 5.444C10.58 16.295 9 18.044 9 21h0a6 6 0 0 1-6-6c0-2.17.974-4.103 2.5-5.444C7.42 7.705 9 5.956 9 3h0z"/>
              </svg>
              <span>Pricing</span>
            </Link>

            {/* Settings Button */}
            <Link
              prefetch
              href="/settings"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg backdrop-blur-md text-white font-medium border shadow-md ${
                currentPath === "/settings" || currentPath === "/settings/" 
                  ? "bg-white/30 border-white/40 cursor-not-allowed" 
                  : "bg-white/10 border-white/20 hover:bg-white/20 hover:shadow-lg hover:scale-105 transition-all duration-300"
              }`}
              aria-disabled={currentPath === "/settings" || currentPath === "/settings/"}
              tabIndex={currentPath === "/settings" || currentPath === "/settings/" ? -1 : 0}
              onClick={e => (currentPath === "/settings" || currentPath === "/settings/") && e.preventDefault()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <span>Settings</span>
            </Link>

            {/* Logout/Login Button - Always shows one or the other */}
            {isLoggedIn ? (
              <button
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 backdrop-blur-md text-white hover:bg-white/20 font-medium border border-white/20 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 w-[205.5px] group"
                onClick={logout}
              >
                <div className="h-6 w-6 flex-shrink-0 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold text-xs shadow-inner border border-white/30">
                  {session?.user?.email?.charAt(0).toUpperCase() || "U"}
                </div>
                <span className="hidden md:inline max-w-[130px] truncate overflow-hidden text-ellipsis whitespace-nowrap group-hover:hidden">
                  {session?.user?.email?.split("@")[0] || "User"}
                </span>
                <span className="hidden md:hidden group-hover:md:inline">Logout</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-shrink-0 hidden md:inline ml-auto"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line
                    x1="21"
                    y1="12"
                    x2="9"
                    y2="12"/>
                </svg>
              </button>
            ) : (
              <Link
                prefetch
                href="/sign-in"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg backdrop-blur-md text-white font-medium border shadow-md w-[205.5px] justify-center ${
                  currentPath === "/sign-in" 
                    ? "bg-white/30 border-white/40 cursor-not-allowed" 
                    : "bg-white/10 border-white/20 hover:bg-white/20 hover:shadow-lg hover:scale-105 transition-all duration-300"
                }`}
                aria-disabled={currentPath === "/sign-in"}
                tabIndex={currentPath === "/sign-in" ? -1 : 0}
                onClick={e => currentPath === "/sign-in" && e.preventDefault()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-shrink-0"
                >
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line
                    x1="15"
                    y1="12"
                    x2="3"
                    y2="12"/>
                </svg>
                <span>Login</span>
              </Link>
            )}
          </div>
        </div>
      </header>
    </>
  );
};